/**
 * Hover / touch / focus prefetch of a job page's `index.pageContext.json`, so the SRP -> job page
 * soft navigation doesn't wait on a fresh server round trip (~200-350 ms measured on prod) after
 * the click.
 *
 * Why not Vike's own `prefetch: { pageContext }` setting: in vike 0.4.264 the pageContext half of
 * it is gated to Vike's docs site (`setPageContextPrefetchCache` asserts `isBrilloutDocpress()`),
 * so enabling it throws on every hover. And this app passes pageContextInit values (user,
 * userState, ...) to the client, which makes Vike fetch pageContext from the server on every
 * navigation anyway, so a client-side data() hook can't skip the request either.
 *
 * So instead: a delegated listener starts the same GET Vike would make, and a narrow wrapper
 * around window.fetch hands that in-flight (or finished) response to Vike's own request for the
 * exact same URL. Each prefetched response is used at most once and only within PREFETCH_TTL_MS,
 * so a stale user/userState snapshot can't linger; anything else falls through to a normal fetch.
 */

const PREFETCH_TTL_MS = 30_000;
const MAX_ENTRIES = 24;
/** Mouse has to rest on a link this long before we spend a request on it. */
const HOVER_INTENT_DELAY_MS = 80;

const PAGE_CONTEXT_SUFFIX = '/index.pageContext.json';
/** Job detail route as rendered by Link (locale-prefixed): /{locale}/{job|tyopaikka|jobb}/{jobId}/{companyId}/{seoSlug}. */
const JOB_DETAIL_PATH_RE = /^\/(?:en|fi|sv)\/(?:job|tyopaikka|jobb)\/[^/]+\/[^/]+\/[^/]+\/?$/;

type CacheEntry = { at: number; promise: Promise<Response> };

const cache = new Map<string, CacheEntry>();
let originalFetch: typeof fetch | null = null;
let installed = false;

/** Same transform as Vike's getPageContextRequestUrl (urlToFile), minus the hash. */
export function toPageContextRequestUrl(href: string, base: string): string | null {
  try {
    const url = new URL(href, base);
    const path = url.pathname.endsWith('/') ? `${url.pathname}index.pageContext.json` : `${url.pathname}${PAGE_CONTEXT_SUFFIX}`;
    return `${url.origin}${path}${url.search}`;
  } catch {
    return null;
  }
}

function cacheKey(input: RequestInfo | URL): string | null {
  let raw: string;
  if (typeof input === 'string') raw = input;
  else if (input instanceof URL) raw = input.href;
  else return null; // Vike passes a plain string; never touch Request objects
  if (!raw.includes('.pageContext.json')) return null;
  try {
    const url = new URL(raw, window.location.href);
    url.hash = '';
    return url.href;
  } catch {
    return null;
  }
}

function pruneCache(now: number): void {
  for (const [key, entry] of cache) {
    if (now - entry.at >= PREFETCH_TTL_MS) cache.delete(key);
  }
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/** Start fetching the pageContext JSON for a job page link (no-op if already fresh in the cache). */
export function prefetchPageContextForHref(href: string): void {
  if (!installed || !originalFetch || typeof window === 'undefined') return;
  const requestUrl = toPageContextRequestUrl(href, window.location.href);
  if (!requestUrl) return;
  const now = Date.now();
  pruneCache(now);
  const existing = cache.get(requestUrl);
  if (existing && now - existing.at < PREFETCH_TTL_MS) return;
  const promise = originalFetch(requestUrl);
  // A failed prefetch must never surface as an unhandled rejection; the navigation retries itself.
  promise.catch(() => {});
  cache.set(requestUrl, { at: now, promise });
}

function isSaveDataOn(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

function jobAnchorFromEventTarget(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.target && anchor.target !== '_self') return null;
  // Only links that get a client-side navigation: Vike treats an absolute href as external and
  // lets the browser do a full page load (e.g. the job page's similar-jobs cards), which never
  // uses the prefetched JSON, so prefetching those would only cost a request.
  const rawHref = anchor.getAttribute('href') ?? '';
  if (!rawHref.startsWith('/') || rawHref.startsWith('//')) return null;
  if (anchor.origin !== window.location.origin) return null;
  if (!JOB_DETAIL_PATH_RE.test(anchor.pathname)) return null;
  if (anchor.pathname === window.location.pathname) return null;
  return anchor;
}

/**
 * Wrap window.fetch once and attach the delegated listeners. Client-only; safe to call more than
 * once. Covers every job link (SRP cards, similar jobs, company pages), not just the B2 card.
 */
export function installPageContextPrefetch(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  originalFetch = window.fetch.bind(window);
  const fetchFallback = originalFetch;

  window.fetch = function fetchWithPageContextPrefetch(input: RequestInfo | URL, init?: RequestInit) {
    const method = init?.method?.toUpperCase();
    if (!method || method === 'GET') {
      const key = cacheKey(input);
      const entry = key ? cache.get(key) : undefined;
      if (key && entry) {
        cache.delete(key); // single use: the next navigation to this URL fetches fresh
        if (Date.now() - entry.at < PREFETCH_TTL_MS) {
          return entry.promise.then(
            (response) => (response.ok ? response : fetchFallback(input, init)),
            () => fetchFallback(input, init),
          );
        }
      }
    }
    return fetchFallback(input, init);
  } as typeof fetch;

  if (isSaveDataOn()) return;

  let hoverTimer: ReturnType<typeof setTimeout> | null = null;
  let hoverAnchor: HTMLAnchorElement | null = null;
  const cancelHover = () => {
    if (hoverTimer) clearTimeout(hoverTimer);
    hoverTimer = null;
    hoverAnchor = null;
  };

  document.addEventListener(
    'pointerover',
    (event) => {
      if ((event as PointerEvent).pointerType !== 'mouse') return; // touch is handled by pointerdown
      const anchor = jobAnchorFromEventTarget(event.target);
      if (!anchor || anchor === hoverAnchor) return;
      cancelHover();
      hoverAnchor = anchor;
      hoverTimer = setTimeout(() => {
        hoverTimer = null;
        prefetchPageContextForHref(anchor.href);
      }, HOVER_INTENT_DELAY_MS);
    },
    { passive: true },
  );
  document.addEventListener(
    'pointerout',
    (event) => {
      if (!hoverAnchor) return;
      const related = (event as PointerEvent).relatedTarget;
      if (related instanceof Node && hoverAnchor.contains(related)) return;
      cancelHover();
    },
    { passive: true },
  );
  const prefetchNow = (event: Event) => {
    // A modified or non-primary press opens a new tab/window (a full page load), which never
    // uses the prefetched JSON.
    if (event instanceof PointerEvent && (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    const anchor = jobAnchorFromEventTarget(event.target);
    if (anchor) prefetchPageContextForHref(anchor.href);
  };
  document.addEventListener('pointerdown', prefetchNow, { passive: true, capture: true });
  document.addEventListener('focusin', prefetchNow, { passive: true });
}

/** Test-only: reset module state. */
export function __resetPageContextPrefetchForTests(): void {
  cache.clear();
  if (installed && originalFetch) window.fetch = originalFetch;
  originalFetch = null;
  installed = false;
}
